import { Link, Outlet } from "react-router-dom";
import { Mail, MapPin, Phone } from "lucide-react";
import { useSite } from "@/context/SiteContext";
import { CAT_KEYS, LISTING_ANCHORS, lines, media } from "@/lib/api";
import { Header } from "./Header";

const FootLink = ({ to, children, testid }) => (
  <li>
    <Link to={to} data-testid={testid} className="text-sm text-stone-300 hover:text-white transition-colors">{children}</Link>
  </li>
);

const Footer = ({ c }) => {
  const f = c.footer;
  return (
    <footer className="bg-charcoal text-stone-300 border-t-4 border-crimson" data-testid="site-footer">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-14 grid gap-10 sm:grid-cols-2 lg:grid-cols-4">
        <div>
          <img src={media(c.site.logo)} alt="IHD" className="h-11 w-auto mb-5" />
          <p className="text-sm leading-relaxed text-stone-400" data-testid="footer-description">{f.description}</p>
        </div>
        <div>
          <h4 className="text-white text-lg mb-4">{f.menu_title}</h4>
          <ul className="space-y-2">
            {lines(f.menu_lines).map((l, i) => {
              const [label, url] = l.split("|");
              return <FootLink key={i} to={(url || "/").trim()} testid={`footer-menu-${i}`}>{label.trim()}</FootLink>;
            })}
          </ul>
        </div>
        <div>
          <h4 className="text-white text-lg mb-4">{f.listing_title}</h4>
          <ul className="space-y-2">
            {CAT_KEYS.map((k) => (
              <FootLink key={k} to={`/listing-options#${LISTING_ANCHORS[k]}`} testid={`footer-listing-${LISTING_ANCHORS[k]}`}>{c[k].footer_label}</FootLink>
            ))}
          </ul>
        </div>
        <div>
          <h4 className="text-white text-lg mb-4">{f.contact_title}</h4>
          <div className="flex gap-3 text-sm mb-3">
            <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-brass" />
            <div>
              <div className="text-white">{c.contact.company}</div>
              {lines(c.contact.address_lines).map((l, i) => <div key={i}>{l}</div>)}
            </div>
          </div>
          <a href={`mailto:${c.contact.email}`} className="flex gap-3 text-sm mb-3 hover:text-white break-all" data-testid="footer-email"><Mail className="w-4 h-4 mt-0.5 shrink-0 text-brass" />{c.contact.email}</a>
          <a href={`tel:${c.contact.phone.replace(/\s/g, "")}`} className="flex gap-3 text-sm hover:text-white" data-testid="footer-phone"><Phone className="w-4 h-4 mt-0.5 shrink-0 text-brass" />{c.contact.phone}</a>
        </div>
      </div>
      <div className="border-t border-white/10 py-5 text-center text-xs text-stone-500 px-4" data-testid="footer-copyright">{f.copyright}</div>
    </footer>
  );
};

export const SiteLayout = () => {
  const { content } = useSite();
  if (!content) return <div className="min-h-screen flex items-center justify-center text-stone-400" data-testid="site-loading">Loading…</div>;
  return (
    <div className="min-h-screen flex flex-col bg-ivory">
      <Header />
      <main className="flex-1">
        <Outlet />
      </main>
      <Footer c={content} />
    </div>
  );
};

export const PageHeader = ({ title, subtitle, testid = "page-title" }) => (
  <section className="bg-ivory-alt border-b border-stone-200">
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
      <span className="block h-[2px] w-12 bg-brass mb-5" />
      <h1 className="text-3xl sm:text-4xl lg:text-5xl text-charcoal break-words" data-testid={testid}>{title}</h1>
      {subtitle && <p className="mt-3 text-stone-600 text-base">{subtitle}</p>}
    </div>
  </section>
);
